import * as THREE from "three";
import { viewportSize } from '../utils/ui.js';
import { isModuleToModuleConnection } from "../utils/geometry.js";
import { CollisionSystem } from "./CollisionSystem.js";
import { SnapSystem } from "./SnapSystem.js";
import { SHOW_DEV_TOOLS } from "../config.js";
import { installMethods } from "../utils/installMethods.js";
import { componentDragMethods } from "./interaction/componentDrag.js";
import { moduleDragMethods } from "./interaction/moduleDrag.js";
import { placementGhostMethods } from "./interaction/placementGhost.js";
import { rotationMethods } from "./interaction/rotation.js";

// Constants
const DRAG_THRESHOLD = 5; // Minimum pixel movement to start dragging
// Timed here rather than read from `dblclick`, which touch WebViews don't fire; taps reach onCanvasClick too.
const DOUBLE_CLICK_MS = 400;

// Input types that keep focus after a click but consume no typing.
const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox", "radio", "range", "color", "button", "submit", "reset", "file", "image",
]);

const acceptsTextInput = (target) => {
  if (!target) return false;
  if (target.isContentEditable || target.tagName === "TEXTAREA") return true;
  return target.tagName === "INPUT" && !NON_TEXT_INPUT_TYPES.has(target.type);
};

export class EventManager {
  constructor(app) {
    this.app = app;

    // Dedicated sub-systems
    this.collisionSystem = new CollisionSystem(app);
    this.snapSystem = new SnapSystem(app);

    // Mouse/touch state
    this.isDragging = false;
    this.isDraggingValid = false;
    this.mouseDownPosition = null;
    this.touchStartPosition = null;
    this.justFinishedDragging = false; // Prevent click from firing right after drag

    // Master toggle for module-to-module snapping (Settings → Snapping).
    // When false, findClosestModuleAttachment returns null so modules drag freely.
    this.snappingEnabled = true;

    // Snap state during dragging
    this.dragSnapTarget = null; // Attachment point we're currently snapped to while dragging
    this.previousDragSnapTarget = null; // Previous snap target for clearing highlights

    // Multi-select dragging state
    this.initialDragPositions = []; // Store initial positions of all selected items
    this.detachedModuleLinks = []; // {child, ap, side} the drag cut, restored if it is put back
    this.dragStartPosition = null; // World position where drag started
    this.dragSnapChildAttachmentSide = null; // Store which child attachment side to use for drag snapping
    // Stable rotation reference for snap SELECTION. The dragged piece / ghost gets
    // auto-rotated each frame to fit the snapped face; reading that rotation back into
    // the next frame's snap search makes the chosen face oscillate (the connector
    // metric is rotation-dependent). These hold the user's intended rotation instead,
    // changed only at drag start and on explicit R-key rotation.
    this.dragSelectRotation = null;     // captured at drag start, cleared on drag end
    this.ghostSelectRotation = null;    // tracked per ghost placement
    this._ghostRotRef = null;           // identity of the ghost the rotation belongs to
    this.lastValidGroupDelta = null; // Last collision-free delta for multi-select, prevents jump-back on collision
    // Per-move scratch for onMouseMove's drag paths.
    this._groundHits = [];
    this._dragDelta = new THREE.Vector3();
    this._dragTestPos = new THREE.Vector3();
    this._dragPosition = new THREE.Vector3();

    // Drag ghost for existing furniture snapping preview
    this.dragGhostFurniture = null; // Ghost furniture shown when dragging existing furniture near snap points
    this.isDragGhostActive = false; // Track if drag ghost is being shown
    this._dragGhostPending = false; // A drag ghost is being cloned (once per drag)
    this.dragSnapIsInvalid = false; // True while the dragged piece sits where it can't be dropped

    // Component drag state (panels, backrests, cushions) — shared by both
    // re-drag (existing component) and bar-drag (new from catalog) paths
    this.componentDragOriginalAP = null;
    this.componentDragOriginalLocalPos = null;
    this.componentDragSubtree = null;       // Dragged component + descendants (never a re-attach target)
    this.componentDragOriginalPos = null;
    this.componentDragOriginalQuat = null;
    this.componentDragTarget = null;       // Current AP the ghost is snapped to
    this.componentDragSlidOffset = null;    // Slide offset in parent-local space

    // Pre-allocated scratch objects for component collision/sliding
    this._ancestorSet = new Set();
    this._slideOffset = new THREE.Vector3();
    this._redragSlidOffset = new THREE.Vector3();
    this._slidingLocalCursor = new THREE.Vector3();

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    // Cache ground mesh to avoid repeated scene-graph traversal
    this.groundMesh = null;

    // Cache furniture meshes for raycasting (invalidated on add/remove)
    this.cachedFurnitureMeshes = null;
    this.furnitureMeshesDirty = true;

    // Scratch objects for component sliding/drag — allocated once, reused every call
    this._slidingPlaneNormal = new THREE.Vector3(0, 1, 0);
    this._slidingPlane = new THREE.Plane();
    this._slidingIntersection = new THREE.Vector3();
    this._componentSnapPos = new THREE.Vector3();
    this._componentSnapQuat = new THREE.Quaternion();
  }

  init() {
    this.setupEventListeners();
  }

  clearAllAttachmentHighlights() {
    const furnitureManager = this.app.getFurnitureManager();
    const allFurniture = furnitureManager?.getAllFurniture() || [];

    allFurniture.forEach((furnitureElement) => {
      if (furnitureElement.attachmentPoints) {
        furnitureElement.attachmentPoints.forEach((attachmentPoint) => {
          if (attachmentPoint.isHighlighted) {
            attachmentPoint.setHighlight(false);
          }
        });
      }
    });

    // Clear tracking variables
    this.dragSnapTarget = null;
    this.previousDragSnapTarget = null;
    this.dragSnapChildAttachmentSide = null;

    // Clear drag ghost if active
    this.clearDragGhost();
  }

  /**
   * Create a raycaster from a mouse/touch event
   * @param {MouseEvent|TouchEvent} event - The mouse or touch event
   * @returns {object} Object containing raycaster and normalized mouse coordinates
   */
  getRaycasterFromEvent(event) {
    const cameraManager = this.app.getCameraManager();
    const rect = this.app.getRenderer().domElement.getBoundingClientRect();

    // Reuse cached mouse vector instead of creating new one
    this.mouse.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );

    // Reuse cached raycaster instead of creating new one
    this.raycaster.setFromCamera(this.mouse, cameraManager.getCamera());

    return { raycaster: this.raycaster, mouse: this.mouse };
  }

  /**
   * Lazily fetch and cache the ground mesh to avoid repeated scene-graph traversals.
   * @returns {THREE.Object3D|null}
   */
  getGroundMesh() {
    if (!this.groundMesh) {
      this.groundMesh = this.app.getScene().getObjectByName("ground");
    }
    return this.groundMesh;
  }

  /**
   * Point under the cursor: a furniture surface when `preferFurniture` (components sit at
   * arm/seat height, where a ground hit would add a vertical gap to the AP distance), else the ground.
   * @returns {THREE.Vector3|null}
   */
  _cursorHit(raycaster, preferFurniture) {
    if (preferFurniture) {
      const meshHits = raycaster.intersectObjects(this.getFurnitureMeshes());
      if (meshHits.length > 0) return meshHits[0].point;
    }
    return this._intersectGround(raycaster)[0]?.point ?? null;
  }

  // Reuses one hit array — every caller reads hits[0].point straight away.
  _intersectGround(raycaster) {
    this._groundHits.length = 0;
    const ground = this.getGroundMesh();
    return ground ? raycaster.intersectObject(ground, true, this._groundHits) : this._groundHits;
  }

  /**
   * Mark the furniture mesh cache as stale so it is rebuilt on next access.
   * Call whenever furniture is added or removed.
   */
  invalidateFurnitureMeshCache() {
    this.furnitureMeshesDirty = true;
    this.cachedFurnitureMeshes = null;
  }

  /**
   * Return cached list of all furniture meshes for raycasting.
   * Rebuilds the list when dirty (furniture added/removed).
   * @returns {THREE.Mesh[]}
   */
  getFurnitureMeshes() {
    if (!this.furnitureMeshesDirty && this.cachedFurnitureMeshes) {
      return this.cachedFurnitureMeshes;
    }

    const furnitureManager = this.app.getFurnitureManager();
    const allFurniture = furnitureManager?.getAllFurniture() || [];
    const meshes = [];

    allFurniture.forEach((furnitureElement) => {
      if (furnitureElement.mesh) {
        furnitureElement.mesh.traverse((child) => {
          if (child.isMesh && !child.userData.isOutline) {
            child.userData.furnitureElement = furnitureElement;
            meshes.push(child);
          }
        });
      }
    });

    this.cachedFurnitureMeshes = meshes;
    this.furnitureMeshesDirty = false;
    return meshes;
  }

  // ===== COLLISION & SNAP DELEGATION =====
  // These thin wrappers keep existing call sites working while the real
  // logic lives in CollisionSystem and SnapSystem.

  clampPositionToRoomBounds(furniture, position) { return this.collisionSystem.clampPositionToRoomBounds(furniture, position); }
  clampDeltaForGroup(furniturePositions, delta) { return this.collisionSystem.clampDeltaForGroup(furniturePositions, delta); }
  clampGhostPosition(ghostFurniture, position) { return this.collisionSystem.clampGhostPosition(ghostFurniture, position); }
  isSnapPositionInBounds(furniture, position) { return this.collisionSystem.isSnapPositionInBounds(furniture, position); }
  checkFurnitureCollisions(furniture, position, excludeSet, overrideRotation) { return this.collisionSystem.checkFurnitureCollisions(furniture, position, excludeSet, overrideRotation); }
  checkGhostFurnitureCollisions(ghostFurniture, position, excludeSet) { return this.collisionSystem.checkGhostFurnitureCollisions(ghostFurniture, position, excludeSet); }
  checkSnapPenetration(child, position, childRotation, parent, oriented = false) { return this.collisionSystem.checkSnapPenetration(child, position, childRotation, parent, oriented); }

  /**
   * A "curve connection" is a snap onto a curved-edge attachment point — the
   * matacao3 SM/SG corner anchors, identified by a non-null `facingAngle` on the
   * parent AP. These join two curved pieces along an arc, so an axis-aligned AABB
   * around a diagonal/curved piece is far larger than the piece itself and the
   * standard (AABB) penetration test reports 3–29 cm of overlap even on a clean
   * touch. So curve connections route the penetration test through its oriented
   * (SAT) path instead — see checkSnapPenetration's `oriented` flag — which uses the
   * pieces' true rotated collision-box slices and therefore still rejects a module
   * dropped on top of a sibling while passing legitimate angled touches. Returning
   * true here means "use the oriented test", not "skip the test".
   */
  // A connection is "curved" (→ oriented SAT penetration test instead of AABB)
  // when EITHER face at the seam is angled. Keying off the parent AP alone misses
  // the case of a curved/diagonal child snapping onto a parent's straight cardinal
  // face — the child's AABB-enclosed angled slices then over-report penetration and
  // the snap is wrongly rejected (e.g. diagon-lol onto diagon-stl's cardinal side).
  _isCurveConnection(parentAP, childFacingAngle = null) {
    return parentAP?.facingAngle != null || childFacingAngle != null;
  }

  /**
   * The inside-placement half of Unity's CanFitInPosition: a snap is rejected when the piece
   * would sink into the target's parent or any other module of that parent's group. Every
   * snap site needs it because their collision checks exclude the whole parent group — a
   * snap that tag-matches an outer AP can geometrically land inside a group sibling (R1's
   * tag-0 long side onto another R's tag-707737 short side lands inside the corner's
   * neighbour). MAX_PARENT_PENETRATION (0.15 m) still allows face-touching adjacency.
   */
  _snapPenetratesGroup(piece, snap, target, group = target.parentFurniture.getAllGroupModules()) {
    const parent = target.parentFurniture;
    const curveConn = this._isCurveConnection(target, snap.childFacingAngle);
    if (this.checkSnapPenetration(piece, snap.position, snap.rotation, parent, curveConn)) return true;
    for (const sib of group) {
      if (sib === parent || sib === piece) continue;
      if (this.checkSnapPenetration(piece, snap.position, snap.rotation, sib, curveConn)) return true;
    }
    return false;
  }
  resolveModuleCollision(furniture, desiredPosition) { return this.collisionSystem.resolveModuleCollision(furniture, desiredPosition); }
  findClosestModuleAttachment(position, excludeFurniture, childAttachmentPoints, currentRotation = 0, specialCaseSnapping = false) { if (!this.snappingEnabled) return null; return this.snapSystem.findClosestModuleAttachment(position, excludeFurniture, childAttachmentPoints, currentRotation, specialCaseSnapping); }
  calculateSnapPosition(attachmentPoint, furnitureAttachmentPoints, currentRotation, furniturePosition, requiredRotation = null) { return this.snapSystem.calculateSnapPosition(attachmentPoint, furnitureAttachmentPoints, currentRotation, furniturePosition, requiredRotation); }
  findClosestComponentAttachment(position, furnitureData, excludeFurniture = null, ray = null) { return this.snapSystem.findClosestComponentAttachment(position, furnitureData, excludeFurniture, ray); }

  // ===== EVENT SETUP =====
  setupEventListeners() {
    // Bind resize handler once
    this.boundResizeHandler = this.onWindowResize.bind(this);

    // Window resize
    window.addEventListener("resize", this.boundResizeHandler);

    // Handle orientation changes on mobile devices
    // Modern API
    if (screen.orientation) {
      screen.orientation.addEventListener("change", this.boundResizeHandler);
    }
    // Legacy fallback for older devices
    window.addEventListener("orientationchange", () => {
      // Small delay to ensure device has finished rotating
      setTimeout(this.boundResizeHandler, 100);
    });

    // Visual viewport API for better mobile support (handles browser chrome, keyboard, etc.)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.boundResizeHandler);
    }

    // Embed/easy mode is read-only: skip every furniture-interaction listener so only
    // the camera (OrbitControls owns its own canvas listeners) responds to input.
    // Resize/orientation listeners above stay wired so the canvas keeps sizing correctly.
    if (this.app.isEmbedMode) return;

    // Get renderer element
    const canvas = this.app.getRenderer().domElement;

    // Mouse events for furniture interaction
    canvas.addEventListener("click", this.onCanvasClick.bind(this));
    canvas.addEventListener("mousedown", this.onMouseDown.bind(this));
    canvas.addEventListener("mousemove", this.onMouseMove.bind(this));
    canvas.addEventListener("mouseup", this.onMouseUp.bind(this));

    // The context menu is suppressed document-wide in main.js.

    // Touch events for mobile
    // Use { passive: false } because we call preventDefault() in onTouchMove to prevent scrolling while dragging
    canvas.addEventListener("touchstart", this.onTouchStart.bind(this), { passive: false });
    canvas.addEventListener("touchmove", this.onTouchMove.bind(this), { passive: false });
    canvas.addEventListener("touchend", this.onTouchEnd.bind(this), { passive: false });

    // Keyboard shortcuts
    document.addEventListener("keydown", this.onKeyDown.bind(this));
  }

  // ===== MOUSE EVENTS =====
  onCanvasClick(event) {
    // Prevent click from firing if we're dragging or just finished dragging
    if (this.isDragging || this.justFinishedDragging) {
      this.justFinishedDragging = false; // Reset the flag
      return;
    }

    // Clear any lingering highlights from previous drag operations
    this.clearAllAttachmentHighlights();

    const furnitureManager = this.app.getFurnitureManager();
    const uiManager = this.app.getUIManager();

    const { raycaster, mouse } = this.getRaycasterFromEvent(event);

    // Check if we're in placement mode (ghost furniture is visible)
    const ghostFurniture = furnitureManager?.getGhostFurniture();
    const isInPlacementMode = ghostFurniture && ghostFurniture.isVisible;

    // Cached furniture meshes; a click on a plus selects the piece under it.
    const furnitureMeshes = isInPlacementMode ? [] : this.getFurnitureMeshes();

    // Check for door clicks
    const sceneManager = this.app.getSceneManager();
    const doorManager = sceneManager?.getDoorManager();
    if (doorManager) {
      const doorMeshes = doorManager.getDoorMeshes();
      if (doorMeshes.length > 0) {
        const doorIntersects = raycaster.intersectObjects(doorMeshes, false);
        if (doorIntersects.length > 0) {
          this.app.getUIManager()?.reopenLayoutSelectionModal();
          return;
        }
      }
    }

    // Check for furniture selection (only if NOT in placement mode)
    if (!isInPlacementMode) {
      const intersects = raycaster.intersectObjects(furnitureMeshes);

      if (intersects.length > 0) {
        // Select furniture
        const selectedMesh = intersects[0].object;
        const furnitureElement = selectedMesh.userData.furnitureElement;

        // Check for multi-select (Ctrl/Cmd/Shift key)
        const multiSelect = event.ctrlKey || event.metaKey || event.shiftKey;

        // For multi-select: only allow selecting modules, not components (panels, cushions, etc.)
        if (multiSelect) {
          // Check if clicked item is a module
          const clickedIsModule = !furnitureElement.furnitureData?.componentType ||
                                  furnitureElement.furnitureData?.componentType === "module";

          if (!clickedIsModule) {
            return;
          }

          // Check if any currently selected items are components
          const selectedFurnitureArray = furnitureManager?.getSelectedFurnitureArray() || [];
          const hasComponentSelected = selectedFurnitureArray.some(f => {
            const componentType = f.furnitureData?.componentType;
            return componentType && componentType !== "module";
          });

          if (hasComponentSelected) {
            return;
          }
        }

        // A second click on the same piece selects it with its components.
        const now = performance.now();
        const isDoubleClick = !multiSelect &&
          this._lastClick?.furniture === furnitureElement &&
          now - this._lastClick.time < DOUBLE_CLICK_MS;
        this._lastClick = isDoubleClick ? null : { furniture: furnitureElement, time: now };

        furnitureManager?.selectFurniture(furnitureElement, multiSelect, { withComponents: isDoubleClick });
        uiManager?.updateSelection(furnitureManager.getSelectedFurnitureArray());
      } else {
        this._lastClick = null;
        // Clicked on ground/empty space (not on furniture)
        // Deselect current furniture (unless holding Ctrl/Cmd/Shift)
        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
          furnitureManager?.selectFurniture(null);
          uiManager?.updateSelection(null);
        }
      }
    }
  }

  onMouseDown(event) {
    // Store mouse down position
    this.mouseDownPosition = {
      x: event.clientX,
      y: event.clientY,
    };

    // Check if we're clicking on any selected furniture to start dragging
    const furnitureManager = this.app.getFurnitureManager();
    const selectedFurnitureArray = furnitureManager?.getSelectedFurnitureArray() || [];

    if (selectedFurnitureArray.length > 0) {
      const { raycaster, mouse } = this.getRaycasterFromEvent(event);

      // Check if we're clicking on any of the selected furniture
      // Filter the cached mesh list to only selected items for correct drag initiation
      const selectedSet = new Set(selectedFurnitureArray);
      const furnitureMeshes = this.getFurnitureMeshes().filter(
        child => selectedSet.has(child.userData.furnitureElement)
      );

      const intersects = raycaster.intersectObjects(furnitureMeshes);

      if (intersects.length > 0) {
        const clickedFurniture = intersects[0].object.userData.furnitureElement;

        // For multi-select: Find root furniture pieces (not attached to anything in the selection)
        // For single-select: Use original logic
        if (selectedFurnitureArray.length > 1) {
          // Find root pieces: furniture that are either:
          // 1. Not attached to anything
          // 2. Attached via module-to-module connection (these are peers, not parent-child)
          // 3. Attached to something outside the selection
          const rootFurniture = selectedFurnitureArray.filter(f => {
            if (!f.attachedTo) return true; // Not attached to anything

            // Check if this is a module-to-module connection
            const isModuleConnection = isModuleToModuleConnection(f, f.attachedTo.parentFurniture);
            if (isModuleConnection) {
              // Module-to-module connections are peers - both are roots
              return true;
            }

            // For component attachments, check if parent is in the selection
            const parentInSelection = selectedFurnitureArray.some(sf => sf === f.attachedTo.parentFurniture);
            return !parentInSelection; // Root if parent is not in selection
          });

          if (rootFurniture.length === 0) {
            return;
          }

          // Store initial positions for root furniture only (children will move automatically)
          this.initialDragPositions = rootFurniture.map(f => ({
            furniture: f,
            position: f.getMesh().position.clone()
          }));
          this.lastValidGroupDelta = null;

          // Get ground intersection to store drag start position
          const groundIntersects = this._intersectGround(raycaster);
          if (groundIntersects.length > 0) {
            this.dragStartPosition = groundIntersects[0].point.clone();
          }
        } else {
          // Single selection: original logic
          const selectedFurniture = selectedFurnitureArray[0];
          if (selectedFurniture.attachedTo) {
            const thisIsModule = selectedFurniture.furnitureData?.componentType === "module";
            const isModuleConnection = isModuleToModuleConnection(
              selectedFurniture,
              selectedFurniture.attachedTo.parentFurniture
            );
            const isComponent = !thisIsModule;

            if (!isModuleConnection && !isComponent) {
              return;
            }
          }

          // Store ground intersection so single-select drag can compute an offset,
          // preventing the item from jumping to the cursor ray's ground hit in 3D view.
          const groundIntersects = this._intersectGround(raycaster);
          if (groundIntersects.length > 0) {
            this.dragStartPosition = groundIntersects[0].point.clone();
          }
        }

        // We're clicking on selected furniture, prepare for dragging
        this.isDraggingValid = true;
        this.app.getCameraManager().getControls().enabled = false;
        event.preventDefault();
      }
    }
  }

  onMouseMove(event) {
    // Compute raycaster once and reuse for all hit-tests this frame
    const { raycaster, mouse } = this.getRaycasterFromEvent(event);
    this.updateGhostFurniturePreview(raycaster);

    // Refresh debug AP overlay colours live while dragging
    this.app.getFurnitureManager()?._debugRefreshModuleAPs?.();

    // Check for door hover
    this.checkDoorHover(event);

    // Check if we should start dragging (mouse moved enough from initial position)
    if (this.isDraggingValid && !this.isDragging && this.mouseDownPosition) {
      const deltaX = Math.abs(event.clientX - this.mouseDownPosition.x);
      const deltaY = Math.abs(event.clientY - this.mouseDownPosition.y);

      // Start dragging if mouse moved more than threshold
      if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
        this.isDragging = true;
        this.app.getCameraManager().getControls().enabled = false;

        this._beginDrag();
      }
    }

    // Early return if we're not dragging furniture
    const furnitureManager = this.app.getFurnitureManager();
    const selectedFurnitureArray = furnitureManager?.getSelectedFurnitureArray() || [];
    if (!this.isDragging || selectedFurnitureArray.length === 0) return;

    // Furniture is moving with the cursor this frame — keep its shadows tracking it.
    this.app.getSceneManager()?.requestShadowUpdate();

    // Handle multi-select dragging
    if (selectedFurnitureArray.length > 1 && this.initialDragPositions.length > 0) {
      this._moveMultiSelection(raycaster, furnitureManager);
      return;
    }

    // Single selection dragging
    const selectedFurniture = selectedFurnitureArray[0];
    if (!selectedFurniture) return;

    // Component drag with ghost preview (panels, backrests, cushions)
    if (this.componentDragOriginalAP) {
      this._moveComponent(selectedFurniture, raycaster, furnitureManager);
      return;
    }

    this._moveModule(selectedFurniture, raycaster, furnitureManager);
  }

  onMouseUp(event) {
    // Always re-enable controls when mouse is released
    this.app.getCameraManager().getControls().enabled = true;

    // Check if mouse moved at all since mousedown
    if (this.mouseDownPosition && event) {
      const deltaX = Math.abs(event.clientX - this.mouseDownPosition.x);
      const deltaY = Math.abs(event.clientY - this.mouseDownPosition.y);
      // If mouse moved more than 2 pixels, consider it a drag and block the subsequent click
      // (2 pixels threshold accounts for natural hand jitter during clicks)
      if (deltaX > 2 || deltaY > 2) {
        this.justFinishedDragging = true;
      }
    }

    // Get selected furniture before clearing state
    const selectedFurniture = this.app.getFurnitureManager()?.getSelectedFurniture();

    // Handle component drag completion (panels, backrests, cushions)
    if (this.componentDragOriginalAP && selectedFurniture) {
      this._dropComponent(selectedFurniture);
      return;
    }

    this._dropModule(selectedFurniture);
  }

  // ===== TOUCH EVENTS =====
  onTouchStart(event) {
    event.preventDefault();
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      this.touchStartPosition = { x: touch.clientX, y: touch.clientY };
      this.onMouseDown({
        clientX: touch.clientX,
        clientY: touch.clientY,
        button: 0,
        preventDefault() {},
      });
    }
  }

  onTouchMove(event) {
    event.preventDefault();
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      this.onMouseMove({
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
    }
  }

  onTouchEnd(event) {
    event.preventDefault();
    if (event.changedTouches && event.changedTouches.length > 0) {
      const touch = event.changedTouches[0];
      const touchX = touch.clientX;
      const touchY = touch.clientY;

      this.onMouseUp({ clientX: touchX, clientY: touchY });

      // Synthesize click for taps (preventDefault blocks native click from touch)
      // Deferred to next frame so the touchend handler returns fast
      if (this.touchStartPosition) {
        const dx = Math.abs(touchX - this.touchStartPosition.x);
        const dy = Math.abs(touchY - this.touchStartPosition.y);
        if (dx < DRAG_THRESHOLD && dy < DRAG_THRESHOLD) {
          this.justFinishedDragging = false;
          requestAnimationFrame(() => {
            this.onCanvasClick({ clientX: touchX, clientY: touchY });
          });
        }
        this.touchStartPosition = null;
      }
    } else {
      this.onMouseUp({ clientX: 0, clientY: 0 });
      this.touchStartPosition = null;
    }
  }

  // ===== KEYBOARD EVENTS =====
  onKeyDown(event) {
    // Viewport shortcuts must not fire while typing; Escape still passes — it cancels
    // drag state, not text. Checkboxes/sliders/colour swatches keep focus after a click,
    // so only text-accepting fields may swallow a shortcut.
    if (event.code !== "Escape" && acceptsTextInput(event.target)) return;

    const furnitureManager = this.app.getFurnitureManager();
    const selectedFurnitureArray = furnitureManager?.getSelectedFurnitureArray() || [];
    const selectedFurniture = selectedFurnitureArray.length > 0 ? selectedFurnitureArray[0] : null;

    switch (event.code) {
      case "KeyD":
        // Debug: toggle module AP overlay — localhost only
        if (SHOW_DEV_TOOLS) furnitureManager?.debugToggleModuleAPs();
        break;

      case "KeyL":
        // Debug, localhost only: opens the path-tracing light rig console.
        if (SHOW_DEV_TOOLS) this.app.ensureLightRigConsole?.().then((c) => c?.toggle());
        break;

      case "KeyP":
        // Debug, localhost only: the toolbar toggle owns the busy, 2D and WebGPU guards.
        if (SHOW_DEV_TOOLS && !event.ctrlKey && !event.metaKey) {
          document.getElementById("path-tracing-btn")?.click();
        }
        break;

      case "KeyA":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          this.app.getUIManager()?.selectAllFurniture();
        }
        break;

      case "Delete":
      case "Backspace":
        if (furnitureManager.deleteSelected()) this.app.getUIManager()?.updateSelection(null);
        break;
      case "Escape":
        this.cancelCurrentInteraction();
        break;
      case "KeyR":
        this._rotateSelectionOrGhost(selectedFurnitureArray, selectedFurniture, furnitureManager);
        break;
      case "KeyV":
        if (!event.ctrlKey && !event.metaKey) {
          // Deselect furniture when switching modes for clarity
          if (this.app.getFurnitureManager()?.getSelectedFurniture()) {
            this.app.getFurnitureManager()?.selectFurniture(null);
            this.app.getUIManager()?.updateSelection(null);
          }
          
          this.app.getCameraManager().toggleSceneMode(
            () => {
              // Transition start callback
              this.app.getUIManager()?.onSceneModeTransitionStart();
            },
            (mode) => {
              // Transition complete callback
              this.app.getUIManager()?.onSceneModeChanged(mode);
            }
          );
        }
        break;
    }
  }

  // ===== WINDOW EVENTS =====
  onWindowResize() {
    const renderer = this.app.getRenderer();
    if (!renderer) return;

    // The canvas container, not the window — Layout 2's sheet insets it. Measured once and
    // handed on: reading clientWidth again would force a second reflow.
    const size = viewportSize();
    const { width, height } = size;

    this.app.getCameraManager().onWindowResize(size);

    // Update renderer size - pass false to avoid CSS conflicts
    renderer.setSize(width, height, false);

    // The device's cap, not a hardcoded 2: this also fires on rotation and keyboard show.
    this.app.getSceneManager().applyPixelRatio();

    // Force a render to update the canvas immediately
    const cameraManager = this.app.getCameraManager();
    if (cameraManager) {
      const camera = cameraManager.getCamera();
      const sceneManager = this.app.getSceneManager();
      if (camera && sceneManager) {
        sceneManager.render(camera);
      }
    }
  }

  checkDoorHover(event) {
    const sceneManager = this.app.getSceneManager();
    const doorManager = sceneManager?.getDoorManager();
    if (!doorManager) return;

    const { raycaster } = this.getRaycasterFromEvent(event);
    const doorMeshes = doorManager.getDoorMeshes();

    if (doorMeshes.length > 0) {
      const doorIntersects = raycaster.intersectObjects(doorMeshes, false);
      const isHovering = doorIntersects.length > 0;

      // Update hover state if changed
      if (isHovering !== doorManager.isHovered) {
        doorManager.setHovered(isHovering);
      }
    }
  }

  /**
   * Unwind one level of in-scene interaction: a component drag, then a module drag, then
   * ghost placement, then selection. Shared by the Escape key and the Android hardware
   * back button so the two can never drift apart.
   *
   * @returns {boolean} true if something was actually cancelled.
   */
  cancelCurrentInteraction() {
    const furnitureManager = this.app.getFurnitureManager();

    if (this.componentDragOriginalAP) {
      const draggedFurniture = furnitureManager?.getSelectedFurniture();
      if (draggedFurniture) {
        this.clearDragGhost();
        this.restoreComponentToOriginal(draggedFurniture);
      }
      this.resetComponentDragState();
      furnitureManager?.invalidateAttachmentMarkers();
      furnitureManager?.updateSelectionOutline();
      return true;
    }

    let handled = false;

    if (this.isDragging) {
      this.clearDragGhost();
      // A cancelled module drag puts everything back where it began, as a cancelled
      // component drag does — otherwise the piece stays wherever the cursor left it.
      this._restoreDraggedModules();
      this.isDragging = false;
      this.isDraggingValid = false;
      this.initialDragPositions = [];
      this.detachedModuleLinks = [];
      this.dragSnapTarget = null;
      this.dragSnapIsInvalid = false;
      this.dragSelectRotation = null;
      this.app.getCameraManager().getControls().enabled = true;
      this.app.notifyStateChanged();
      handled = true;
    }

    if (furnitureManager?.getGhostFurniture()) {
      const uiManager = this.app.getUIManager();
      if (uiManager) uiManager.cleanupAfterPlacement(furnitureManager);
      else furnitureManager.clearGhostFurniture();
      handled = true;
    } else {
      // Nothing in flight — fall back to deselecting.
      if (furnitureManager?.getSelectedFurniture()) handled = true;
      furnitureManager?.selectFurniture(null);
      this.app.getUIManager()?.updateSelection(null);
    }

    this.clearAllAttachmentHighlights();
    return handled;
  }
}

installMethods(EventManager.prototype, componentDragMethods, moduleDragMethods, placementGhostMethods, rotationMethods);
